//GoogleMapComponent.tsx
/// <reference types="google.maps" />
'use client';

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Entity, Address, Region, BoundsSearchRequest, BoundsSearchResponse, ClusterPoint, MapClusterState } from '../types';
import { useRouter } from 'next/navigation';
import MapCampsiteCard from './MapCampsiteCard';
import { googleMapsBoundsToLocalBounds, createDebouncedBoundsSearch } from '../lib/searchEntities';
import {
  createSuperCluster,
  entitiesToGeoJSON,
  getClustersInBounds,
  googleBoundsToClusterBounds,
  getClusterExpansionZoom,
  getClusterBounds,
  shouldCluster,
  MarkerUpdateBatcher,
  ZOOM_THRESHOLDS
} from '../lib/clustering';
import { createClusterMarkerElement } from './ClusterMarker';
import SuperCluster from 'supercluster';
import './ClusterAnimations.css';
import { PlusOutlined } from '@ant-design/icons';
import { DEFAULT_FALLBACK_IMAGE } from '../utils/normalizeImageSrc';
import { getOptimizedEntityImage } from '../utils/entityImage';

// Proxy-aware fallback: the bare DEFAULT_FALLBACK_IMAGE (/images/…) won't
// resolve through the Shopify proxy.  This prefixed version is used for
// the map popup card where the URL is consumed directly by an <img> tag.
const MAP_FALLBACK_IMAGE = '/apps/campsite-explorer/images/default-campsite-hero.jpg';

/**
 * Combine two arrays of entities, removing duplicates by ID
 * Prioritizes entities from the first array (initial search results)
 */
const combineUniqueEntities = (initialEntities: Entity[], boundsEntities: Entity[]): Entity[] => {
  const entityMap = new Map<number, Entity>();
  
  // Add initial entities first (these take priority)
  initialEntities.forEach(entity => {
    if (entity && entity.id) {
      entityMap.set(entity.id, entity);
    }
  });
  
  // Add bounds entities only if they don't already exist
  boundsEntities.forEach(entity => {
    if (entity && entity.id && !entityMap.has(entity.id)) {
      entityMap.set(entity.id, entity);
    }
  });
  
  return Array.from(entityMap.values());
};

const hasUsableImageSource = (src?: string | null): boolean => {
  if (typeof src !== 'string') {
    return false;
  }
  return src.trim().length > 4;
};

const getEntityImageSignalScore = (entity?: Entity | null): number => {
  if (!entity) {
    return -1;
  }

  if (entity.gallery_images?.some((img) => hasUsableImageSource(img?.image_src))) {
    return 3;
  }

  if (hasUsableImageSource(entity.image_src)) {
    return 2;
  }

  if (hasUsableImageSource(entity.display_src)) {
    return 1;
  }

  return 0;
};

const mergeEntityMedia = (preferred: Entity, fallback: Entity): Entity => ({
  ...fallback,
  ...preferred,
  gallery_images:
    preferred.gallery_images && preferred.gallery_images.length > 0
      ? preferred.gallery_images
      : fallback.gallery_images,
  image_src: hasUsableImageSource(preferred.image_src) ? preferred.image_src : fallback.image_src,
  display_src: hasUsableImageSource(preferred.display_src) ? preferred.display_src : fallback.display_src,
});

type RouteConfig = {
  start: string;
  end: string;
  waypoints?: string[];
  color?: string;
  strokeWeight?: number;
  maxDeviationKm?: number;
  routeKey?: string | null;
};

interface GoogleMapComponentProps {
  entities?: Entity[]; // Initial entities to display (combined with bounds loading when enabled)
  regions?: Region[];
  onMarkerClick?: (name: string) => string;
  onEntitySelected?: (entity: Entity) => void;
  onMapChange?: (bounds: google.maps.LatLngBounds | null) => void;
  onMapReady?: (map: google.maps.Map) => void;
  showCampsites?: boolean;
  center?: { lat: number; lng: number };
  zoom?: number;
  initialCenter?: { lat: number; lng: number };
  initialZoom?: number;
  selectedRegion?: Region | null;
  maxZoom?: number;
  // New props for bounds-based loading
  useBoundsLoading?: boolean; // Enable dynamic bounds-based entity loading
  boundsSearchFilters?: {
    features?: number[];
    [key: string]: any;
  };
  onEntitiesLoaded?: (entities: Entity[]) => void; // Callback when entities are loaded
  onLoadingStateChange?: (loading: boolean) => void; // Callback for loading state
  // New props for clustering
  enableClustering?: boolean; // Enable marker clustering (default: true)
  clusterRadius?: number; // Cluster radius in pixels
  onClusterClick?: (clusterId: string | number, bounds: google.maps.LatLngBounds) => void;
  // Trip planner integration
  onAddTripStop?: (entity: Entity) => void;
  tripStopEntityIds?: Set<number>;
  // Route rendering
  routeConfig?: RouteConfig | null;
  onRouteDrawn?: (result: google.maps.DirectionsResult) => void;
}

/**
 * A unified map component that can display both regions and campsites
 */
const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({
  entities = [],
  regions = [],
  onMarkerClick,
  onEntitySelected,
  onMapChange,
  onMapReady,
  showCampsites = false,
  center,
  zoom,
  initialCenter = { lat: -25.2744, lng: 133.7751 }, // Center of Australia
  initialZoom = 4,
  selectedRegion = null,
  maxZoom = 15,
  // New bounds loading props
  useBoundsLoading = false,
  boundsSearchFilters,
  onEntitiesLoaded,
  onLoadingStateChange,
  routeConfig = null,
  onRouteDrawn,
  onAddTripStop,
  tripStopEntityIds,
  // New clustering props
  enableClustering = true,
  clusterRadius = 60,
  onClusterClick,
}) => {
  // Add comprehensive logging at the start
  console.log('[Map] GoogleMapComponent render START', { 
    timestamp: new Date().toISOString(),
    entitiesCount: entities?.length || 0, 
    regionsCount: regions?.length || 0, 
    showCampsites, 
    initialCenter, 
    initialZoom,
    selectedRegion: selectedRegion?.name || 'none',
    maxZoom,
    hasOnMarkerClick: !!onMarkerClick,
    hasOnMapChange: !!onMapChange,
    isClient: typeof window !== 'undefined',
    hasGoogle: typeof window !== 'undefined' && !!window.google
  });

  // Early return for SSR
  if (typeof window === 'undefined') {
    console.log('[Map] SSR detected, returning loading placeholder');
    return (
      <div className="relative w-full h-full min-h-[480px] rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
        <div className="text-center p-4">
          <div className="animate-pulse bg-gray-300 w-16 h-16 rounded mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  const router = useRouter();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<Entity | Region | null>(null);
  const [clickedMarkers, setClickedMarkers] = useState<Set<number>>(new Set());
  const [componentError, setComponentError] = useState<string | null>(null);
  
  // New state for bounds-based loading
  const [boundsEntities, setBoundsEntities] = useState<Entity[]>([]);
  const [isBoundsLoading, setIsBoundsLoading] = useState<boolean>(false);
  const [boundsError, setBoundsError] = useState<string | null>(null);

  const entityImageIndex = useMemo(() => {
    const index = new Map<number, Entity>();

    const ingest = (candidate?: Entity) => {
      if (!candidate || typeof candidate.id !== 'number') {
        return;
      }

      const existing = index.get(candidate.id);
      if (!existing) {
        index.set(candidate.id, candidate);
        return;
      }

      const existingScore = getEntityImageSignalScore(existing);
      const candidateScore = getEntityImageSignalScore(candidate);

      if (candidateScore > existingScore) {
        index.set(candidate.id, mergeEntityMedia(candidate, existing));
      } else if (candidateScore === existingScore) {
        index.set(candidate.id, mergeEntityMedia(existing, candidate));
      }
    };

    (entities || []).forEach(ingest);
    (boundsEntities || []).forEach(ingest);

    return index;
  }, [entities, boundsEntities]);

  const getBestEntityVariant = useCallback(
    (entity: Entity): Entity => {
      if (!entity || typeof entity.id !== 'number') {
        return entity;
      }

      const cached = entityImageIndex.get(entity.id);
      if (!cached) {
        return entity;
      }

      const cachedScore = getEntityImageSignalScore(cached);
      const incomingScore = getEntityImageSignalScore(entity);

      if (cachedScore > incomingScore) {
        return cached;
      }

      if (incomingScore > cachedScore) {
        return mergeEntityMedia(entity, cached);
      }

      return mergeEntityMedia(cached, entity);
    },
    [entityImageIndex]
  );

  // When clustering is disabled (trip view), clear any previously cached bounds entities
  useEffect(() => {
    if (!enableClustering) {
      setBoundsEntities([]);
    }
  }, [enableClustering]);

  // When bounds loading is disabled (e.g. route search active), clear stale bounds entities
  // so SuperCluster only contains the route search results
  useEffect(() => {
    if (!useBoundsLoading) {
      setBoundsEntities([]);
    }
  }, [useBoundsLoading]);

  // New state for clustering
  const [clusterState, setClusterState] = useState<MapClusterState>({
    clusters: [],
    zoom: initialZoom,
    bounds: null,
    expandedClusterId: null
  });
  
  const mapRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<Map<number, any>>(new Map());
  const mapInitializedRef = useRef<boolean>(false);
  
  // Clustering refs
  const superclusterRef = useRef<SuperCluster | null>(null);
  const clusterMarkersRef = useRef<Map<string | number, any>>(new Map());
  const markerUpdateBatcherRef = useRef<MarkerUpdateBatcher | null>(null);

  // When clustering is switched off (trip view), remove any existing cluster markers
  useEffect(() => {
    if (enableClustering) return;

    if (clusterMarkersRef.current.size > 0) {
      clusterMarkersRef.current.forEach((marker, id) => {
        try {
          if (marker && marker.map) {
            marker.map = null;
          }
        } catch (error) {
          console.error(`[Map] Error clearing cluster marker ${id}:`, error);
        }
      });
      clusterMarkersRef.current.clear();
    }

    if (markerUpdateBatcherRef.current) {
      markerUpdateBatcherRef.current.clear();
    }
  }, [enableClustering]);
  
  // Refs for bounds loading
  const debouncedBoundsSearchRef = useRef<((request: BoundsSearchRequest) => void) | null>(null);
  const lastBoundsRequestRef = useRef<BoundsSearchRequest | null>(null);
  
  // Clustering animation state
  const lastZoomRef = useRef<number>(initialZoom);
  const clusterTransitionTimeoutRef = useRef<number | null>(null);
  
  // Route rendering refs
  const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);
  const routePolylineRef = useRef<google.maps.Polyline | null>(null);
  const routeHaloRef = useRef<google.maps.Polyline | null>(null);
  const routeBoundsRef = useRef<google.maps.LatLngBounds | null>(null);
  const lastRouteSignatureRef = useRef<string | null>(null);
  const lastRouteEndpointsRef = useRef<string | null>(null);
  const shouldFitRouteRef = useRef<boolean>(true);
  const hasUserInteractedRef = useRef<boolean>(false);
  const suppressBoundsEventsRef = useRef<boolean>(false);
  const boundsSuppressionTimeoutRef = useRef<number | null>(null);
  const shouldAutoFitMarkersRef = useRef<boolean>(true);
  const lastMarkerSignatureRef = useRef<string>('');
  const isEntityItem = (item: Entity | Region): item is Entity =>
    Boolean(item) && Object.prototype.hasOwnProperty.call(item, 'addresses');
  
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || 'AIzaSyAd6J-M2idmQ_dBi3tfD8CGPws_cpYvDEM';
  const mapId = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || 'f1f81d2941d6d32e';

  const suppressBoundsEvents = useCallback((
    mapInstance: google.maps.Map | null,
    operation: () => void,
    afterIdle?: (release: () => void) => void
  ) => {
    if (!mapInstance) return;

    const release = () => {
      suppressBoundsEventsRef.current = false;
      if (boundsSuppressionTimeoutRef.current !== null) {
        window.clearTimeout(boundsSuppressionTimeoutRef.current);
        boundsSuppressionTimeoutRef.current = null;
      }
    };

    suppressBoundsEventsRef.current = true;

    if (boundsSuppressionTimeoutRef.current !== null) {
      window.clearTimeout(boundsSuppressionTimeoutRef.current);
    }

    const idleListener = google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
      if (boundsSuppressionTimeoutRef.current !== null) {
        window.clearTimeout(boundsSuppressionTimeoutRef.current);
        boundsSuppressionTimeoutRef.current = null;
      }

      if (afterIdle) {
        try {
          afterIdle(release);
        } catch (error) {
          console.error('[Map] Error in suppressed afterIdle callback:', error);
          release();
        }
      } else {
        release();
      }
    });

    boundsSuppressionTimeoutRef.current = window.setTimeout(() => {
      google.maps.event.removeListener(idleListener);
      release();
    }, 1500);

    try {
      operation();
    } catch (error) {
      console.error('[Map] Error during suppressed map operation:', error);
      google.maps.event.removeListener(idleListener);
      release();
    }
  }, []);

  const fitBoundsWithMaxZoom = useCallback((
    mapInstance: google.maps.Map | null,
    targetBounds: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | null,
    padding?: google.maps.Padding,
    zoomLimit?: number
  ) => {
    if (!mapInstance || !targetBounds) return;

    suppressBoundsEvents(
      mapInstance,
      () => {
        if (padding) {
          mapInstance.fitBounds(targetBounds, padding);
        } else {
          mapInstance.fitBounds(targetBounds);
        }
      },
      (release) => {
        try {
          const currentZoom = mapInstance.getZoom();
          console.log('[Map] Current zoom level after fitting bounds:', currentZoom);

          const limit = typeof zoomLimit === 'number' ? zoomLimit : maxZoom;
          if (typeof currentZoom === 'number' && currentZoom > limit) {
            console.log(`[Map] Limiting zoom level from ${currentZoom} to ${limit}`);
            suppressBoundsEvents(mapInstance, () => mapInstance.setZoom(limit));
            return;
          }

          release();
        } catch (error) {
          console.error('[Map] Error adjusting zoom level after fitBounds:', error);
          release();
        }
      }
    );
  }, [maxZoom, suppressBoundsEvents]);

  // Use asymmetric padding on desktop so the route/corridor sits visually centred
  // once the trip sidebar is visible. On mobile/tablet we keep it lighter.
  const getResponsivePadding = useCallback((): google.maps.Padding => {
    const isDesktop =
      typeof window !== 'undefined' ? window.innerWidth >= 1280 : true;
    if (isDesktop) {
      return {
        top: 72,
        right: 72,
        bottom: 72,
        left: routeConfig ? 240 : 72, // nudge content right when a route is shown
      };
    }
    return {
      top: 56,
      right: 56,
      bottom: 56,
      left: routeConfig ? 140 : 56,
    };
  }, [routeConfig]);

  console.log('[Map] Component state:', {
    mapExists: !!map,
    isLoading,
    hasError: !!error,
    componentError,
    mapInitialized: mapInitializedRef.current,
    markersCount: markersRef.current.size,
    selectedItem: selectedItem?.name || 'none',
    clickedMarkersCount: clickedMarkers.size,
    // Bounds loading state
    useBoundsLoading,
    isBoundsLoading,
    boundsEntitiesCount: boundsEntities.length,
    boundsError
  });

  // Add error boundary-like behavior
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('[Map] Window error caught:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
      
      if (event.message.includes('GoogleMapComponent') || event.filename?.includes('GoogleMapComponent')) {
        setComponentError(`Component error: ${event.message}`);
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[Map] Unhandled promise rejection:', event.reason);
      if (String(event.reason).includes('google') || String(event.reason).includes('map')) {
        setComponentError(`Promise rejection: ${event.reason}`);
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Handler for marker clicks with router support
  const handleMarkerAction = useCallback((item: Entity | Region) => {
    try {
      console.log('[Map] handleMarkerAction called for item:', { 
        id: item?.id, 
        name: item?.name, 
        type: item && 'addresses' in item ? 'Entity' : 'Region' 
      });
      
      if (!item) {
        console.error('[Map] handleMarkerAction called with null/undefined item');
        return '';
      }

      const resolvedItem = isEntityItem(item) ? getBestEntityVariant(item) : item;

      // Set the selected item for the info window
      setSelectedItem(resolvedItem);
      
      if (isEntityItem(resolvedItem) && onEntitySelected) {
        try {
          onEntitySelected(resolvedItem);
        } catch (callbackError) {
          console.error('[Map] Error in onEntitySelected callback:', callbackError);
        }
      }
      
      // Add to clicked markers set
      setClickedMarkers((prev) => {
        const newSet = new Set(prev);
        newSet.add(resolvedItem.id);
        console.log('[Map] Updated clicked markers:', Array.from(newSet));
        return newSet;
      });

      // Return URL for navigation if onMarkerClick is provided
      if (onMarkerClick) {
        const url = onMarkerClick(resolvedItem.name);
        console.log('[Map] Using custom onMarkerClick handler, returned URL:', url);
        return url;
      }
      
      // Default URL pattern if no handler provided
      const slug = resolvedItem.name
        ? resolvedItem.name
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
        : 'campsite';

      let url = '';
      if ('addresses' in resolvedItem) {
        // It's an Entity (campsite)
        url = `/apps/campsite-explorer/camp-sites/${slug}`;
      } else {
        // It's a Region
        url = `/apps/campsite-explorer/regions/${slug}`;
      }
      console.log('[Map] Generated default URL:', url);
      return url;
    } catch (error) {
      console.error('[Map] Error in handleMarkerAction:', error);
      setComponentError(`Marker action error: ${error}`);
      return '';
    }
  }, [getBestEntityVariant, onEntitySelected, onMarkerClick]);

  const getEntityImageUrl = useCallback(
    (entity: Entity): string => {
      try {
        const resolvedEntity = getBestEntityVariant(entity);
        const url = getOptimizedEntityImage(resolvedEntity, { width: 820, quality: 78 });
        // If getOptimizedEntityImage returned the bare fallback path, swap it
        // for the proxy-prefixed version so the <img> in MapCampsiteCard can
        // resolve it without an extra failed request.
        if (!url || url === DEFAULT_FALLBACK_IMAGE) {
          return MAP_FALLBACK_IMAGE;
        }
        return url;
      } catch (error) {
        console.error('[Map] Error in getEntityImageUrl:', error);
        return MAP_FALLBACK_IMAGE;
      }
    },
    [getBestEntityVariant]
  );

  /** Derive a campsite type label from an entity's features */
  const determineCampsiteType = useCallback((entity: Entity): string => {
    const feats = entity.features || [];
    const isFree = feats.some(f =>
      f.desc?.toLowerCase().includes('free') || f.desc?.toLowerCase().includes('no cost')
    );
    if (isFree) return 'Free Camping';
    if (feats.some(f => f.desc?.toLowerCase().includes('caravan'))) return 'Caravan Park';
    if (feats.some(f => f.desc?.toLowerCase().includes('cabin'))) return 'Holiday Park';
    return 'Campsite';
  }, []);

  const getValidCoordinates = useCallback((address: Address | undefined): google.maps.LatLngLiteral | null => {
    try {
      console.log('[Map] getValidCoordinates called with address:', address);
      
      if (!address || !address.gps_latitude || !address.gps_longitude) {
        console.log('[Map] Invalid address or missing coordinates');
        return null;
      }
      
      const lat = parseFloat(address.gps_latitude);
      const lng = parseFloat(address.gps_longitude);
      
      console.log('[Map] Parsed coordinates:', { lat, lng });
      
      // Validate coordinates are within range
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        console.log('[Map] Coordinates out of valid range');
        return null;
      }
      
      console.log('[Map] Valid coordinates returned');
      return { lat, lng };
    } catch (error) {
      console.error('[Map] Error in getValidCoordinates:', error);
      return null;
    }
  }, []);

  const getRegionCoordinates = useCallback((region: Region): google.maps.LatLngLiteral | null => {
    try {
      console.log('[Map] getRegionCoordinates called for region:', { id: region?.id, name: region?.name });
      
      if (!region || !region.latitude || !region.longitude) {
        console.log('[Map] Region missing coordinates');
        return null;
      }
      
      const lat = parseFloat(String(region.latitude));
      const lng = parseFloat(String(region.longitude));
      
      console.log('[Map] Parsed region coordinates:', { lat, lng });
      
      // Validate coordinates are within range
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        console.log('[Map] Region coordinates out of valid range');
        return null;
      }
      
      console.log('[Map] Valid region coordinates returned');
      return { lat, lng };
    } catch (error) {
      console.error('[Map] Error in getRegionCoordinates:', error);
      return null;
    }
  }, []);

  const createMarkerIcon = useCallback((id: number, isRegion: boolean = false) => {
    try {
      console.log('[Map] createMarkerIcon called:', { id, isRegion, isClicked: clickedMarkers.has(id) });
      
      if (typeof document === 'undefined') {
        console.log('[Map] Document not available (server-side rendering)');
        return null;
      }
      
      const div = document.createElement('div');
      div.className = 'marker';
      div.setAttribute('role', 'button');
      div.setAttribute('aria-label', isRegion ? 'Region marker' : 'Campsite marker');
      div.setAttribute('tabindex', '0');
      
      // Style the marker
      div.style.width = '24px';
      div.style.height = '24px';
      div.style.borderRadius = '50%';
      div.style.backgroundColor = clickedMarkers.has(id) ? '#808080' : (isRegion ? '#e63946' : '#4a90e2');
      div.style.border = '3px solid #ffffff';
      div.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      div.style.boxSizing = 'border-box';
      div.style.cursor = 'pointer';
      
      console.log('[Map] Marker icon created successfully');
      return div;
    } catch (error) {
      console.error('[Map] Error creating marker icon:', error);
      return null;
    }
  }, [clickedMarkers]);

  // Initialize clustering system
  const initializeClustering = useCallback(() => {
    console.log('[Map] Initializing clustering system');
    
    if (!markerUpdateBatcherRef.current) {
      markerUpdateBatcherRef.current = new MarkerUpdateBatcher();
    }
    
    const clusterOptions = {
      radius: clusterRadius,
      maxZoom: ZOOM_THRESHOLDS.CLUSTER_ALL,
      minPoints: 2,
    };
    
    superclusterRef.current = createSuperCluster(clusterOptions);
    console.log('[Map] SuperCluster instance created');
  }, [clusterRadius]);

  // Update clustering data when entities change
  const updateClusteringData = useCallback((entities: Entity[]) => {
    if (!superclusterRef.current || !enableClustering) return;
    
    console.log(`[Map] Updating clustering data with ${entities.length} entities`);
    
    try {
      const geoJsonPoints = entitiesToGeoJSON(entities);
      console.log(`[Map] Generated ${geoJsonPoints.length} GeoJSON points`);
      
      // Ensure we have valid GeoJSON points before loading
      if (geoJsonPoints.length === 0) {
        console.log('[Map] No valid GeoJSON points to load');
        return;
      }
      
      superclusterRef.current.load(geoJsonPoints);
      console.log(`[Map] Successfully loaded ${geoJsonPoints.length} points into SuperCluster`);
    } catch (error) {
      console.error('[Map] Error updating clustering data:', error, entities);
    }
  }, [enableClustering]);

  // Get clusters for current map view
  const getClustersForView = useCallback((map: google.maps.Map): ClusterPoint[] => {
    if (!superclusterRef.current || !enableClustering) return [];
    
    const bounds = map.getBounds();
    const zoom = map.getZoom();
    
    if (!bounds || zoom === undefined) {
      console.log('[Map] No bounds or zoom available');
      return [];
    }
    
    try {
      const clusterBounds = googleBoundsToClusterBounds(bounds);
      console.log(`[Map] Getting clusters for bounds: ${clusterBounds}, zoom: ${zoom}`);
      
      const clusters = getClustersInBounds(superclusterRef.current, clusterBounds, zoom);
      
      console.log(`[Map] Generated ${clusters.length} clusters for zoom ${zoom}`);
      return clusters;
    } catch (error) {
      console.error('[Map] Error getting clusters for view:', error);
      return [];
    }
  }, [enableClustering]);

  // Handle cluster marker click
  const handleClusterClick = useCallback((clusterId: string | number, cluster: ClusterPoint) => {
    if (!map || !superclusterRef.current) return;
    
    console.log('[Map] Cluster clicked:', { clusterId, pointCount: cluster.properties.point_count });
    
    try {
      // Get expansion zoom level
      const expansionZoom = getClusterExpansionZoom(superclusterRef.current, clusterId);
      const currentZoom = map.getZoom() || 0;
      
      if (expansionZoom <= currentZoom) {
        // If expansion zoom won't help, get cluster bounds and fit to them
        const clusterBounds = getClusterBounds(superclusterRef.current, clusterId);
        if (clusterBounds) {
          console.log('[Map] Fitting to cluster bounds');
          map.fitBounds(clusterBounds);
          onClusterClick?.(clusterId, clusterBounds);
        }
      } else {
        // Zoom to expansion level
        console.log(`[Map] Zooming to expansion level ${expansionZoom}`);
        const [lng, lat] = cluster.geometry.coordinates;
        map.setCenter({ lat, lng });
        map.setZoom(expansionZoom);
        onClusterClick?.(clusterId, map.getBounds()!);
      }
      
      // Update expanded cluster state for animation
      setClusterState(prev => ({
        ...prev,
        expandedClusterId: clusterId
      }));
      
      // Reset expanded state after animation
      setTimeout(() => {
        setClusterState(prev => ({
          ...prev,
          expandedClusterId: null
        }));
      }, 500);
    } catch (error) {
      console.error('[Map] Error handling cluster click:', error);
    }
  }, [map, onClusterClick]);

  // Create or update cluster markers on map
  const updateClusterMarkers = useCallback((clusters: ClusterPoint[]) => {
    if (!map) return;
    
    console.log(`[Map] Updating cluster markers: ${clusters.length} clusters`);
    
    // Clear existing cluster markers
    clusterMarkersRef.current.forEach((marker, id) => {
      try {
        if (marker && marker.map) {
          marker.map = null;
        }
      } catch (error) {
        console.error(`[Map] Error clearing cluster marker ${id}:`, error);
      }
    });
    clusterMarkersRef.current.clear();
    
    // Create new cluster markers
    clusters.forEach((cluster) => {
      try {
        const [lng, lat] = cluster.geometry.coordinates;
        const position = { lat, lng };
        
        if (cluster.properties.cluster) {
          // It's a cluster
          const clusterId = cluster.properties.clusterId!;
          const pointCount = cluster.properties.point_count!;
          const clusterType = cluster.properties.clusterType || 'mixed';
          const isExpanded = clusterState.expandedClusterId === clusterId;
          
          const clusterElement = createClusterMarkerElement(
            pointCount,
            clusterType,
            clusterId,
            () => handleClusterClick(clusterId, cluster),
            () => {
              // Hover effect
              const element = clusterElement.querySelector('.cluster-marker') as HTMLElement;
              if (element) {
                element.style.transform = 'scale(1.05)';
              }
            },
            () => {
              // Unhover effect
              const element = clusterElement.querySelector('.cluster-marker') as HTMLElement;
              if (element) {
                element.style.transform = isExpanded ? 'scale(1.1)' : 'scale(1)';
              }
            }
          );
          
          // Use AdvancedMarkerElement if available, otherwise fallback
          if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
            const marker = new google.maps.marker.AdvancedMarkerElement({
              map,
              position,
              content: clusterElement,
              title: `Cluster of ${pointCount} campsites`,
            });
            
            clusterMarkersRef.current.set(clusterId, marker);
          } else {
            // Fallback to regular marker (though this won't support custom HTML)
            const marker = new google.maps.Marker({
              map,
              position,
              title: `Cluster of ${pointCount} campsites`,
            });
            
            marker.addListener('click', () => handleClusterClick(clusterId, cluster));
            clusterMarkersRef.current.set(clusterId, marker);
          }
        } else {
          // It's an individual entity - use existing entity marker logic
          const entity = cluster.properties.entity!;
          if (entity) {
            // Use existing addMarker logic but store in cluster markers for consistency
            addIndividualMarker(entity);
          }
        }
      } catch (error) {
        console.error('[Map] Error creating cluster marker:', error);
      }
    });
  }, [map, clusterState.expandedClusterId, handleClusterClick]);

  // Add individual marker (helper for non-clustered entities)
  const addIndividualMarker = useCallback((entity: Entity) => {
    if (!map || !entity.addresses || entity.addresses.length === 0) return;
    
    const coordinates = getValidCoordinates(entity.addresses[0]);
    if (!coordinates) return;
    
    const markerIcon = createMarkerIcon(entity.id, false);
    if (!markerIcon) return;
    
    try {
      if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: coordinates,
          content: markerIcon,
          title: entity.name,
        });
        
        marker.addListener('click', () => {
          handleMarkerAction(entity);
        });
        
        markersRef.current.set(entity.id, marker);
      } else {
        const marker = new google.maps.Marker({
          map,
          position: coordinates,
          title: entity.name,
        });
        
        marker.addListener('click', () => {
          handleMarkerAction(entity);
        });
        
        markersRef.current.set(entity.id, marker);
      }
    } catch (error) {
      console.error('[Map] Error creating individual marker:', error);
    }
  }, [map, getValidCoordinates, createMarkerIcon, handleMarkerAction]);

  // Initialize Google Maps - only run once when the component mounts
  useEffect(() => {
    console.log('[Map] Initialize Google Maps effect triggered');
    
    if (!mapRef.current) {
      console.log('[Map] Map ref not available');
      return;
    }
    
    // Prevent multiple initializations
    if (mapInitializedRef.current) {
      console.log('[Map] Map already initialized, skipping');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setComponentError(null);
    console.log('[Map] Loading Google Maps API with key:', apiKey ? 'Key provided' : 'No key');

    const loader = new Loader({
      apiKey,
      version: 'weekly',
      libraries: ['places', 'marker'],
      mapIds: [mapId],
    });

    loader.load().then(() => {
      console.log('[Map] Google Maps API loaded successfully');
      
      if (!mapRef.current) {
        console.log('[Map] Map ref no longer available');
        return;
      }
      
      try {
        // Calculate initial center and zoom if we have a selected region
        let mapCenter = initialCenter;
        let mapZoom = initialZoom;

        if (selectedRegion) {
          console.log('[Map] Using selected region for initial view:', selectedRegion.name);
          const regionCoords = getRegionCoordinates(selectedRegion);
          if (regionCoords) {
            mapCenter = regionCoords;
            mapZoom = 8; // Closer zoom for viewing a specific region
            console.log('[Map] Set map center to region coordinates:', mapCenter);
          }
        }

        console.log('[Map] Creating new map with settings:', { mapCenter, mapZoom, mapId });
        const newMap = new google.maps.Map(mapRef.current, {
          center: mapCenter,
          zoom: mapZoom,
          mapId: mapId,
          fullscreenControl: true,
          mapTypeControl: true,
          streetViewControl: true,
          zoomControl: true,
          gestureHandling: 'cooperative',
          mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
            position: google.maps.ControlPosition.TOP_RIGHT,
          },
        });
        
        newMap.addListener('dragstart', () => {
          if (suppressBoundsEventsRef.current) return;

          hasUserInteractedRef.current = true;
          shouldFitRouteRef.current = false;
          shouldAutoFitMarkersRef.current = false;
        });
        newMap.addListener('zoom_changed', () => {
          if (suppressBoundsEventsRef.current) return;

          if (mapInitializedRef.current) {
            hasUserInteractedRef.current = true;
            shouldFitRouteRef.current = false;
            shouldAutoFitMarkersRef.current = false;
          }
        });
        
        // Listen for bounds changes
        newMap.addListener('bounds_changed', () => {
          if (suppressBoundsEventsRef.current) {
            console.log('[Map] Skipping bounds_changed handling due to suppression flag');
            return;
          }

          console.log('[Map] Map bounds changed event');
          if (onMapChange) {
            try {
              const bounds = newMap.getBounds() || null;
              console.log('[Map] Calling onMapChange with bounds:', bounds ? 'Bounds object' : 'null');
              onMapChange(bounds);
            } catch (error) {
              console.error('[Map] Error in onMapChange callback:', error);
            }
          }
          
          // Handle clustering updates when map bounds or zoom changes
          if (showCampsites && enableClustering && superclusterRef.current) {
            try {
              const currentZoom = newMap.getZoom() || 0;
              const previousZoom = lastZoomRef.current;
              
              // Check if we've crossed clustering thresholds
              const wasClusteringZoom = shouldCluster(previousZoom);
              const isClusteringZoom = shouldCluster(currentZoom);
              
              if (wasClusteringZoom !== isClusteringZoom) {
                console.log(`[Map] Clustering threshold crossed: ${previousZoom} -> ${currentZoom}`);
                
                // Clear transition timeout if exists
                if (clusterTransitionTimeoutRef.current) {
                  clearTimeout(clusterTransitionTimeoutRef.current);
                }
                
                // Add transition delay for smooth animations
                clusterTransitionTimeoutRef.current = window.setTimeout(() => {
                  const clusters = getClustersForView(newMap);
                  updateClusterMarkers(clusters);
                  
                  setClusterState(prev => ({
                    ...prev,
                    clusters,
                    zoom: currentZoom,
                    bounds: newMap.getBounds() ? googleMapsBoundsToLocalBounds(newMap.getBounds()!) : null
                  }));
                }, 150);
              } else {
                // Normal clustering update
                const clusters = getClustersForView(newMap);
                updateClusterMarkers(clusters);
                
                setClusterState(prev => ({
                  ...prev,
                  clusters,
                  zoom: currentZoom,
                  bounds: newMap.getBounds() ? googleMapsBoundsToLocalBounds(newMap.getBounds()!) : null
                }));
              }
              
              lastZoomRef.current = currentZoom;
            } catch (error) {
              console.error('[Map] Error in clustering update:', error);
            }
          }
          
          // Handle bounds-based entity loading
          if (useBoundsLoading && showCampsites) {
            try {
              const bounds = newMap.getBounds();
              const zoom = newMap.getZoom();
              
              if (bounds && debouncedBoundsSearchRef.current) {
                const localBounds = googleMapsBoundsToLocalBounds(bounds);
                const request: BoundsSearchRequest = {
                  bounds: localBounds,
                  zoom: zoom || 10,
                  limit: 200,
                  filters: boundsSearchFilters || {}
                };
                
                console.log('[Map] Triggering bounds search with:', request);
                lastBoundsRequestRef.current = request;
                setIsBoundsLoading(true);
                onLoadingStateChange?.(true);
                debouncedBoundsSearchRef.current(request);
              }
            } catch (error) {
              console.error('[Map] Error in bounds-based entity loading:', error);
            }
          }
        });
        
        // Set accessibility attributes
        setTimeout(() => {
          const mapDiv = mapRef.current?.querySelector('div[aria-label="Map"]');
          if (mapDiv) {
            console.log('[Map] Setting accessibility attributes on map div');
            mapDiv.setAttribute('role', 'application');
            mapDiv.setAttribute('aria-label', 'Interactive map of campsites and regions');
          }
        }, 100);
        
        mapInitializedRef.current = true;
        console.log('[Map] Map created successfully');
        setMap(newMap);
        if (onMapReady) {
          try {
            onMapReady(newMap);
          } catch (onReadyError) {
            console.error('[Map] Error in onMapReady callback:', onReadyError);
          }
        }
      } catch (error) {
        console.error('[Map] Error initializing Google Map:', error);
        setError('Failed to initialize the map. Please refresh and try again.');
        setComponentError(`Map initialization error: ${error}`);
      } finally {
        setIsLoading(false);
      }
    }).catch((error) => {
      console.error('[Map] Error loading Google Maps API:', error);
      setError('Failed to load Google Maps. Please check your internet connection and try again.');
      setComponentError(`API loading error: ${error}`);
      setIsLoading(false);
    });
    
    // Cleanup function
    return () => {
      console.log('[Map] Cleanup function called - removing map and markers');
      try {
        // Clear regular markers
        if (markersRef.current) {
          markersRef.current.forEach((marker, id) => {
            try {
              if (marker && marker.map) {
                marker.map = null;
              }
            } catch (error) {
              console.error(`[Map] Error cleaning up marker ${id}:`, error);
            }
          });
          markersRef.current.clear();
        }
        
        // Clear cluster markers
        if (clusterMarkersRef.current) {
          clusterMarkersRef.current.forEach((marker, id) => {
            try {
              if (marker && marker.map) {
                marker.map = null;
              }
            } catch (error) {
              console.error(`[Map] Error cleaning up cluster marker ${id}:`, error);
            }
          });
          clusterMarkersRef.current.clear();
        }
        
        // Clean up clustering resources
        if (markerUpdateBatcherRef.current) {
          markerUpdateBatcherRef.current.clear();
          markerUpdateBatcherRef.current = null;
        }
        
        if (clusterTransitionTimeoutRef.current) {
          clearTimeout(clusterTransitionTimeoutRef.current);
          clusterTransitionTimeoutRef.current = null;
        }
        
        if (routeHaloRef.current) {
          routeHaloRef.current.setMap(null);
          routeHaloRef.current = null;
        }

        if (routePolylineRef.current) {
          routePolylineRef.current.setMap(null);
          routePolylineRef.current = null;
        }
        routeBoundsRef.current = null;
        lastRouteSignatureRef.current = null;
        lastRouteEndpointsRef.current = null;
        shouldFitRouteRef.current = true;
        hasUserInteractedRef.current = false;
        shouldAutoFitMarkersRef.current = true;
        lastMarkerSignatureRef.current = '';
        
        superclusterRef.current = null;
        
        // Reset the initialization flag if component unmounts
        mapInitializedRef.current = false;
        setMap(null);
        if (boundsSuppressionTimeoutRef.current !== null) {
          window.clearTimeout(boundsSuppressionTimeoutRef.current);
          boundsSuppressionTimeoutRef.current = null;
        }
        suppressBoundsEventsRef.current = false;
      } catch (error) {
        console.error('[Map] Error in cleanup function:', error);
      }
    };
  }, []); // Only run on mount

  // Sync external center prop with map instance
  useEffect(() => {
    if (!map || !center) return;
    
    const currentCenter = map.getCenter();
    const latDiff = currentCenter ? Math.abs(currentCenter.lat() - center.lat) : Infinity;
    const lngDiff = currentCenter ? Math.abs(currentCenter.lng() - center.lng) : Infinity;
    
    if (latDiff > 0.000001 || lngDiff > 0.000001) {
      map.panTo(center);
    }
  }, [map, center?.lat, center?.lng]);

  // Sync external zoom prop with map instance
  useEffect(() => {
    if (!map || typeof zoom !== 'number') return;
    
    const currentZoom = map.getZoom();
    if (currentZoom !== zoom) {
      map.setZoom(zoom);
    }
  }, [map, zoom]);

  // Draw driving route for trip planner mode
  useEffect(() => {
    if (!map) return;

    if (!routeConfig) {
      if (routeHaloRef.current) {
        routeHaloRef.current.setMap(null);
        routeHaloRef.current = null;
      }
      if (routePolylineRef.current) {
        routePolylineRef.current.setMap(null);
        routePolylineRef.current = null;
      }
      routeBoundsRef.current = null;
      lastRouteSignatureRef.current = null;
      lastRouteEndpointsRef.current = null;
      shouldFitRouteRef.current = true;
      hasUserInteractedRef.current = false;
      return;
    }

    const { start, end } = routeConfig;
    if (!start || !end) return;
    if (typeof window === 'undefined' || !window.google) return;

    const waypoints = routeConfig.waypoints || [];
    const normalize = (value: string) => value.trim().toLowerCase();
    const routeKeySegment = routeConfig.routeKey ? `|${routeConfig.routeKey.toLowerCase()}` : '';
    const signature = [start, ...waypoints, end].map(normalize).join('|') + routeKeySegment;

    if (routePolylineRef.current && signature === lastRouteSignatureRef.current) {
      return;
    }

    lastRouteSignatureRef.current = signature;

    const endpointsSignature = `${normalize(start)}|${normalize(end)}`;
    if (endpointsSignature !== lastRouteEndpointsRef.current) {
      shouldFitRouteRef.current = true;
      hasUserInteractedRef.current = false;
      lastRouteEndpointsRef.current = endpointsSignature;
    } else if (!routePolylineRef.current) {
      shouldFitRouteRef.current = true;
    }

    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new google.maps.DirectionsService();
    }

    const directionsRequest: google.maps.DirectionsRequest = {
      origin: start,
      destination: end,
      travelMode: google.maps.TravelMode.DRIVING,
      provideRouteAlternatives: false,
    };

    if (waypoints.length) {
      directionsRequest.waypoints = waypoints.map((location) => ({
        location,
        stopover: true,
      }));
      directionsRequest.optimizeWaypoints = false;
    }

    directionsServiceRef.current.route(directionsRequest, (result, status) => {
      if (status !== google.maps.DirectionsStatus.OK || !result || !result.routes?.length) {
        console.error('[Map] Directions request failed:', status, result);
        return;
      }

      const primaryRoute = result.routes[0];

      if (routeHaloRef.current) {
        routeHaloRef.current.setMap(null);
        routeHaloRef.current = null;
      }

      if (routePolylineRef.current) {
        routePolylineRef.current.setMap(null);
        routePolylineRef.current = null;
      }

      if (primaryRoute.overview_path?.length) {
        const path = primaryRoute.overview_path;
        const routeColor = routeConfig.color || '#2563eb';
        const haloWeight = Math.min(60, Math.max(14, (routeConfig.maxDeviationKm ?? 60) * 0.6));

        routeHaloRef.current = new google.maps.Polyline({
          path,
          strokeColor: routeColor,
          strokeOpacity: 0.18,
          strokeWeight: haloWeight,
          clickable: false,
          map,
          zIndex: 1,
        });

        routePolylineRef.current = new google.maps.Polyline({
          path,
          strokeColor: routeColor,
          strokeOpacity: 0.85,
          strokeWeight: routeConfig.strokeWeight ?? 4,
          map,
          zIndex: 2,
        });
      }

      if (primaryRoute.bounds) {
        const sw = primaryRoute.bounds.getSouthWest();
        const ne = primaryRoute.bounds.getNorthEast();
        routeBoundsRef.current = new google.maps.LatLngBounds(sw, ne);
      } else {
        routeBoundsRef.current = null;
      }

      if (shouldFitRouteRef.current && routeBoundsRef.current) {
        try {
          fitBoundsWithMaxZoom(map, routeBoundsRef.current, getResponsivePadding());
        } catch (fitError) {
          console.error('[Map] Error fitting map to route bounds:', fitError);
        }
      }

      shouldFitRouteRef.current = !hasUserInteractedRef.current;

      if (onRouteDrawn) {
        try {
          onRouteDrawn(result);
        } catch (callbackError) {
          console.error('[Map] Error in onRouteDrawn callback:', callbackError);
        }
      }
    });
  }, [map, routeConfig, maxZoom, onRouteDrawn, fitBoundsWithMaxZoom]);

  // Initialize clustering when map is ready
  useEffect(() => {
    if (!map || !showCampsites) return;
    
    console.log('[Map] Initializing clustering system');
    initializeClustering();
  }, [map, showCampsites, initializeClustering]);

  // Update clustering data whenever entities change
  useEffect(() => {
    if (!map || !showCampsites) return;
    
    // Combine initial entities with bounds-loaded entities, removing duplicates
    const allowBoundMerging = useBoundsLoading && enableClustering;
    const entitiesToUse = allowBoundMerging
      ? combineUniqueEntities(entities || [], boundsEntities)
      : entities;
    console.log('[Map] Clustering data update triggered:', {
      entityCount: entitiesToUse?.length || 0,
      enableClustering,
      useBoundsLoading
    });
    
    if (entitiesToUse && entitiesToUse.length > 0 && enableClustering) {
      updateClusteringData(entitiesToUse);
      
      // Update clusters for current view
      const clusters = getClustersForView(map);
      updateClusterMarkers(clusters);
      
      setClusterState(prev => ({
        ...prev,
        clusters,
        zoom: map.getZoom() || initialZoom,
        bounds: map.getBounds() ? googleMapsBoundsToLocalBounds(map.getBounds()!) : null
      }));
    }
  }, [
    map, 
    entities, 
    boundsEntities, 
    enableClustering, 
    useBoundsLoading, 
    updateClusteringData, 
    getClustersForView, 
    updateClusterMarkers,
    initialZoom
  ]);

  // Add markers whenever data changes (fallback for non-clustering mode or regions)
  useEffect(() => {
    console.log('[Map] Add markers effect triggered:', { 
      mapExists: !!map,
      entityCount: entities?.length || 0,
      regionCount: regions?.length || 0,
      showCampsites,
      enableClustering,
      hasGoogleMaps: !!(typeof window !== 'undefined' && window.google)
    });
    
    if (!map || !window.google) {
      console.log('[Map] Map not ready or Google Maps not loaded');
      return;
    }

    try {
      // If clustering is enabled for campsites, skip the old marker logic
      if (showCampsites && enableClustering) {
        console.log('[Map] Clustering enabled, skipping old marker logic for campsites');
        return;
      }

      // Clear existing markers
      console.log(`[Map] Clearing ${markersRef.current.size} existing markers`);
      if (markersRef.current) {
        markersRef.current.forEach((marker, id) => {
          try {
            if (marker && marker.map) {
              marker.map = null;
            }
          } catch (error) {
            console.error(`[Map] Error clearing marker ${id}:`, error);
          }
        });
      }
      markersRef.current.clear();

      const bounds = new google.maps.LatLngBounds();
      let hasValidCoordinates = false;
      let markersCreated = 0;

      // Function to create and add a marker
      const addMarker = (item: Entity | Region, isRegion: boolean) => {
        try {
          console.log(`[Map] Adding marker for ${isRegion ? 'region' : 'entity'}:`, { id: item?.id, name: item?.name });
          
          if (!item) {
            console.error('[Map] Cannot add marker for null/undefined item');
            return;
          }
          
          // Get coordinates based on item type
          let coordinates: google.maps.LatLngLiteral | null = null;
          
          if (isRegion) {
            // Handle region
            coordinates = getRegionCoordinates(item as Region);
          } else {
            // Handle entity/campsite
            const entity = item as Entity;
            if (entity.addresses && entity.addresses.length > 0) {
              console.log('[Map] Getting coordinates from entity address');
              coordinates = getValidCoordinates(entity.addresses[0]);
            } else {
              console.log('[Map] Entity has no valid addresses');
            }
          }
          
          if (!coordinates) {
            console.log('[Map] No valid coordinates, skipping marker creation');
            return;
          }
          
          hasValidCoordinates = true;
          console.log('[Map] Extending map bounds with coordinates:', coordinates);
          bounds.extend(coordinates);

          // Create marker based on available Google Maps API version
          if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
            console.log('[Map] Using AdvancedMarkerElement for marker creation');
            // Use advanced marker if available
            const markerIcon = createMarkerIcon(item.id, isRegion);
            
            if (markerIcon) {
              const marker = new google.maps.marker.AdvancedMarkerElement({
                map,
                position: coordinates,
                content: markerIcon,
                title: item.name,
              });

              // Add click listener
              marker.addListener('click', () => {
                console.log('[Map] Advanced marker clicked:', { id: item.id, name: item.name });
                try {
                  handleMarkerAction(item);
                } catch (error) {
                  console.error('[Map] Error handling marker click:', error);
                }
              });
              
              // Store marker reference
              markersRef.current.set(item.id, marker);
              markersCreated++;
            }
          } else {
            console.log('[Map] Using fallback Marker for marker creation');
            // Fallback to regular marker
            const marker = new google.maps.Marker({
              map,
              position: coordinates,
              title: item.name,
              // Add additional styling options here if needed
            });

            // Add click listener
            marker.addListener('click', () => {
              console.log('[Map] Regular marker clicked:', { id: item.id, name: item.name });
              try {
                handleMarkerAction(item);
              } catch (error) {
                console.error('[Map] Error handling marker click:', error);
              }
            });
            
            // Store marker reference
            markersRef.current.set(item.id, marker);
            markersCreated++;
          }
        } catch (error) {
          console.error(`[Map] Error creating marker for ${isRegion ? 'region' : 'entity'} ${item?.id}:`, error);
        }
      };

      const allowBoundsMerge = useBoundsLoading && enableClustering;
      const entitiesToShow = allowBoundsMerge
        ? combineUniqueEntities(entities || [], boundsEntities)
        : entities;

      const markerSignature = showCampsites
        ? (entitiesToShow?.map((entity, index) => (entity?.id ?? `missing-${index}`)).join('|') || '')
        : (regions?.map((region, index) => (region?.id ?? `missing-region-${index}`)).join('|') || '');

      if (markerSignature !== lastMarkerSignatureRef.current) {
        lastMarkerSignatureRef.current = markerSignature;
        shouldAutoFitMarkersRef.current = true;
      }

      // Add markers based on component mode
      if (showCampsites) {
        if (entitiesToShow && entitiesToShow.length > 0) {
          console.log(`[Map] Adding campsite markers for ${entitiesToShow.length} entities (bounds loading: ${useBoundsLoading})`);
          // Show campsite markers
          entitiesToShow.forEach((entity, index) => {
            if (entity) {
              addMarker(entity, false);
            } else {
              console.warn(`[Map] Entity at index ${index} is null/undefined`);
            }
          });
        }
      } else if (regions && regions.length > 0) {
        console.log(`[Map] Adding region markers for ${regions.length} regions`);
        // Show region markers
        regions.forEach((region, index) => {
          if (region) {
            addMarker(region, true);
          } else {
            console.warn(`[Map] Region at index ${index} is null/undefined`);
          }
        });
      }

      console.log(`[Map] Created ${markersCreated} markers successfully`);
      
      // Fit map to bounds or center on Australia if no valid coordinates
      if (hasValidCoordinates && shouldAutoFitMarkersRef.current) {
        console.log('[Map] Fitting map to bounds');
        fitBoundsWithMaxZoom(map, bounds, getResponsivePadding());
        shouldAutoFitMarkersRef.current = false;
      } else if (!hasValidCoordinates) {
        console.log('[Map] No valid coordinates detected after marker pass');

        /**
         * If the parent component supplied an explicit center (e.g. geocode result),
         * preserve that view instead of snapping back to the Australian default.
         * Only fall back to the default map view when we truly have no guidance,
         * such as on the very first paint.
         */
        if (center) {
          console.log('[Map] Preserving provided center since results lacked coordinates');
          map.panTo(center);
          if (typeof zoom === 'number') {
            map.setZoom(zoom);
          }
        } else if (!mapInitializedRef.current) {
          console.log('[Map] No center override available; using default initial view');
          map.setCenter(initialCenter);
          map.setZoom(initialZoom);
        } else {
          console.log('[Map] Map already initialized; leaving current view untouched');
        }

        shouldAutoFitMarkersRef.current = false;
      } else {
        console.log('[Map] Skipping auto-fit due to manual interaction or existing state');
      }
    } catch (error) {
      console.error('[Map] Error in add markers effect:', error);
      setComponentError(`Markers error: ${error}`);
    }
  }, [
    map,
    entities,
    boundsEntities,
    regions,
    showCampsites,
    useBoundsLoading,
    getValidCoordinates,
    getRegionCoordinates,
    createMarkerIcon,
    initialCenter,
    initialZoom,
    maxZoom,
    fitBoundsWithMaxZoom,
    enableClustering,
    handleMarkerAction
  ]);

  // Update marker styles when clicked markers change
  useEffect(() => {
    console.log('[Map] Update marker styles effect triggered');
    
    if (!map) {
      console.log('[Map] Map not available for style updates');
      return;
    }
    
    try {
      const markerCount = markersRef.current.size;
      console.log(`[Map] Updating styles for ${markerCount} markers`);
      
      markersRef.current.forEach((marker, id) => {
        try {
          // Update marker appearance if it's an AdvancedMarkerElement
          if (marker && marker.content && typeof marker.content === 'object') {
            const isRegion = !showCampsites; // Determine marker type based on component mode
            console.log(`[Map] Updating marker ${id} (${isRegion ? 'region' : 'campsite'})`);
            
            const icon = createMarkerIcon(id, isRegion);
            if (icon && marker.content !== icon) { // Only update if different
              marker.content = icon;
            }
          }
        } catch (error) {
          console.error(`[Map] Error updating marker ${id} style:`, error);
        }
      });
    } catch (error) {
      console.error('[Map] Error in marker style update effect:', error);
    }
  }, [clickedMarkers, createMarkerIcon, showCampsites, map]);

  // Handle keyboard events for accessibility
  useEffect(() => {
    console.log('[Map] Keyboard handler effect triggered');
    
    const handleKeyDown = (event: KeyboardEvent) => {
      try {
        if (selectedItem && (event.key === 'Escape' || event.key === 'Esc')) {
          console.log('[Map] Escape key pressed, closing info window');
          setSelectedItem(null);
        }
      } catch (error) {
        console.error('[Map] Error handling keyboard event:', error);
      }
    };

    console.log('[Map] Adding keyboard event listener');
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      console.log('[Map] Removing keyboard event listener');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedItem]);

  // Initialize debounced bounds search effect
  useEffect(() => {
    console.log('[Map] Initialize debounced bounds search effect');
    
    if (!useBoundsLoading) {
      console.log('[Map] Bounds loading disabled, skipping debounced search setup');
      return;
    }
    
    // Create debounced search function
    const handleBoundsSearchResult = (result: BoundsSearchResponse) => {
      console.log('[Map] Bounds search result received:', {
        entitiesCount: result.entities.length,
        error: result.error,
        total: result.total
      });
      
      setIsBoundsLoading(false);
      onLoadingStateChange?.(false);
      
      if (result.error) {
        setBoundsError(result.error);
        setBoundsEntities([]);
        onEntitiesLoaded?.([]);
      } else {
        setBoundsError(null);
        setBoundsEntities(result.entities);
        onEntitiesLoaded?.(result.entities);
      }
    };
    
    const debouncedSearch = createDebouncedBoundsSearch(handleBoundsSearchResult, 300);
    debouncedBoundsSearchRef.current = debouncedSearch;
    
    console.log('[Map] Debounced bounds search initialized');
    
    return () => {
      console.log('[Map] Cleaning up debounced bounds search');
      debouncedBoundsSearchRef.current = null;
    };
  }, [useBoundsLoading, boundsSearchFilters, onEntitiesLoaded, onLoadingStateChange]);

  // Update bounds search when filters change
  useEffect(() => {
    console.log('[Map] Bounds search filters changed:', boundsSearchFilters);
    
    if (!useBoundsLoading || !map || !debouncedBoundsSearchRef.current) {
      return;
    }
    
    // Re-trigger search with new filters if we have previous bounds
    if (lastBoundsRequestRef.current) {
      const updatedRequest: BoundsSearchRequest = {
        ...lastBoundsRequestRef.current,
        filters: boundsSearchFilters || {}
      };
      
      console.log('[Map] Re-triggering bounds search with updated filters');
      setIsBoundsLoading(true);
      onLoadingStateChange?.(true);
      debouncedBoundsSearchRef.current(updatedRequest);
    }
  }, [boundsSearchFilters, useBoundsLoading, map, onLoadingStateChange]);

  // Handle navigation on marker/item selection
  const handleCardClick = (item: Entity | Region) => {
    try {
      console.log('[Map] handleCardClick called for:', { id: item?.id, name: item?.name });
      
      if (!item) {
        console.error('[Map] handleCardClick called with null/undefined item');
        return;
      }
      
      const url = handleMarkerAction(item);
      console.log('[Map] Navigation URL:', url);
      
      const shouldHandleNavigationLocally = !onEntitySelected;
      if (url && shouldHandleNavigationLocally) {
        console.log('[Map] Navigating to URL (no external handler):', url);
        router.push(url);
      } else if (!shouldHandleNavigationLocally) {
        console.log('[Map] Navigation handled by onEntitySelected callback');
      } else {
        console.log('[Map] No URL returned, not navigating');
      }
    } catch (error) {
      console.error('[Map] Error in handleCardClick:', error);
      setComponentError(`Card click error: ${error}`);
    }
  };

  console.log('[Map] Rendering map component', { 
    timestamp: new Date().toISOString(),
    isLoading, 
    hasError: !!error, 
    componentError,
    mapInitialized: !!map,
    selectedItem: selectedItem?.name || 'none',
    markersCount: markersRef.current.size
  });

  // Show component error if any
  if (componentError) {
    return (
      <div className="relative w-full h-full min-h-[480px] rounded-lg overflow-hidden bg-red-50 flex items-center justify-center">
        <div className="text-center p-4 bg-white rounded-lg shadow-md max-w-md">
          <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Component Error</h3>
          <p className="text-gray-700 mb-4">{componentError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full min-h-[480px] rounded-lg overflow-hidden">
      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-70 z-10">
          <div className="text-center p-4 bg-white rounded-lg shadow-md">
            <div className="animate-spin w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full mx-auto mb-2"></div>
            <p className="text-gray-700">Loading map...</p>
          </div>
        </div>
      )}
      
      {/* Bounds loading indicator */}
      {!isLoading && isBoundsLoading && useBoundsLoading && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-white rounded-lg shadow-md p-3 flex items-center">
            <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full mr-2"></div>
            <p className="text-sm text-gray-700">Loading campsites...</p>
          </div>
        </div>
      )}
      
      {/* Bounds error indicator */}
      {!isLoading && boundsError && useBoundsLoading && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-red-50 border border-red-200 rounded-lg shadow-md p-3 flex items-center">
            <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p className="text-sm text-red-700">Failed to load campsites</p>
          </div>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center p-4 bg-white rounded-lg shadow-md max-w-md">
            <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Map Error</h3>
            <p className="text-gray-700 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )}
      
      {/* Map container */}
      <div ref={mapRef} className="h-full w-full">
        {/* Map will be rendered here */}
      </div>
      
      {/* Info card */}
      {map && selectedItem && (
        <div className="absolute top-2 left-2 right-2 sm:top-4 sm:left-4 sm:right-auto z-20 sm:max-w-sm" role="dialog" aria-modal="true" aria-labelledby="info-title">
          {('addresses' in selectedItem) ? (
            <MapCampsiteCard
              key={selectedItem.id}
              title={selectedItem.name}
              description={selectedItem.short_desc || ''}
              imageUrl={getEntityImageUrl(selectedItem as Entity)}
              location={
                selectedItem.addresses && selectedItem.addresses[0]
                  ? [selectedItem.addresses[0].town, selectedItem.addresses[0].state]
                      .filter(Boolean)
                      .join(', ')
                  : undefined
              }
              isFree={
                (selectedItem as Entity).features?.some(f =>
                  f.desc?.toLowerCase().includes('free') ||
                  f.desc?.toLowerCase().includes('no cost')
                ) ?? false
              }
              url={`/apps/campsite-explorer/camp-sites/${selectedItem.name
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '')}`}
              onClose={() => setSelectedItem(null)}
              onClick={() => handleCardClick(selectedItem)}
              onAddToTrip={
                onAddTripStop
                  ? () => onAddTripStop(selectedItem as Entity)
                  : undefined
              }
              isInTrip={
                typeof selectedItem.id === 'number' &&
                !!tripStopEntityIds?.has(selectedItem.id as number)
              }
              rating={(selectedItem as Entity).rating}
              campsiteType={determineCampsiteType(selectedItem as Entity)}
              features={(selectedItem as Entity).features}
              isPremium={(selectedItem as Entity).is_premium}
            />
          ) : (
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <button
                onClick={() => setSelectedItem(null)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 p-1 rounded-full"
                aria-label="Close information panel"
              >
                x
              </button>
              <h3 id="info-title" className="text-lg font-semibold mb-2">{selectedItem.name}</h3>
              <p className="text-sm text-gray-600">{(selectedItem as Region).short_desc || 'No description available'}</p>
              <button
                onClick={() => handleCardClick(selectedItem)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                View details
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Map controls - custom UI for mobile friendliness */}
      <div className="absolute bottom-4 right-4 z-10 flex flex-col space-y-2">
        <button
          onClick={() => {
            try {
              console.log('[Map] Zoom in button clicked');
              const currentZoom = map?.getZoom();
              if (map && currentZoom !== undefined) {
                const newZoom = currentZoom + 1;
                console.log(`[Map] Changing zoom from ${currentZoom} to ${newZoom}`);
                map.setZoom(newZoom);
              }
            } catch (error) {
              console.error('[Map] Error zooming in:', error);
            }
          }}
          className="bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Zoom in"
          disabled={!map}
        >
          <PlusOutlined className="text-base" />
        </button>
        <button
          onClick={() => {
            try {
              console.log('[Map] Zoom out button clicked');
              const currentZoom = map?.getZoom();
              if (map && currentZoom !== undefined) {
                const newZoom = currentZoom - 1;
                console.log(`[Map] Changing zoom from ${currentZoom} to ${newZoom}`);
                map.setZoom(newZoom);
              }
            } catch (error) {
              console.error('[Map] Error zooming out:', error);
            }
          }}
          className="bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Zoom out"
          disabled={!map}
        >
          <span className="text-2xl font-bold leading-none">-</span>
        </button>
      </div>
    </div>
  );
};

export default GoogleMapComponent;












